import { useRef } from 'react';
import { Link } from 'react-router';
import { Reveal, useInView } from '@components/motion';
import FigmaFrame from './FigmaFrame';
import { FOCUS_AREAS } from '@/data/navigation';
import { PROJECTS } from '@/data/projects';

const SUBHEADING =
    'Our programs are designed to confront injustice directly — supporting individuals, challenging systemic abuse, and advancing reforms that build a fairer and more accountable justice system.';

type Program = {
    /** Greyed lead-in of the title. */
    lead: string;
    /** Bold remainder. */
    bold: string;
    slug: string;
    description: string;
    icon: string;
    photo: string;
    bg: string;
};

/** Photography behind each area card, in the order the areas are listed. */
const AREA_PHOTOS = [
    '/landing/programs/program-photo.webp',
    '/landing/stories/story1.webp',
    '/landing/justice-performance/skyline.webp',
    '/landing/stories/story3.webp',
    '/landing/stories/story4.webp',
];

/**
 * The stack is the focus areas the CMS has placed on the homepage, in slot
 * order — or every area in the menu until it has placed any. Read at render,
 * not at import, so the hydrated menu is what shows. Backgrounds alternate
 * white / #FDFFF9, as designed.
 */
function programs(): Program[] {
    const placed = FOCUS_AREAS.filter((area) => (area.homeSlot ?? 0) > 0);
    return (placed.length ? placed : FOCUS_AREAS).map((area, i) => {
        const slug = area.href.split('/').pop() ?? '';
        // "Tech For Justice" → grey "Tech For", bold "Justice".
        const words = area.label.split(' ');
        return {
            lead: words.length > 1 ? `${words.slice(0, -1).join(' ')} ` : '',
            bold: words[words.length - 1],
            slug,
            description: area.description,
            icon: area.icon,
            photo: AREA_PHOTOS[i % AREA_PHOTOS.length],
            bg: i % 2 === 0 ? '#FFFFFF' : '#FDFFF9',
        };
    });
}

const CARD_H = 534;
/** Design-px height of the pinned heading band; cards pin just below it. */
const HEAD_H = 296;
/** Shared sticky top: every card pins in the SAME frame (just below the heading),
    so each new card slides up and covers the previous one exactly in place. */
const STACK_TOP = `calc(${((HEAD_H / 1920) * 100).toFixed(4)}vw + 8px)`;

/** Shared transition for card-content reveals. */
const RV_BASE = 'transition-[opacity,translate] duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)]';
const at = (ms: number): React.CSSProperties => ({ transitionDelay: `${ms}ms` });

/**
 * Decorative equalizer-style vertical bars rendered beside the card (matching the
 * Figma waveform). Heights are transcribed from the Figma rectangle geometry.
 */
const BAR_HEIGHTS = [247, 263, 263, 263, 263, 263, 263, 219, 203, 199, 203, 211, 219, 223, 263, 263, 254, 263];

function EqualizerBars({ className, style }: { className?: string; style?: React.CSSProperties }) {
    return (
        <div className={`flex items-end gap-[13.3px] ${className ?? ''}`} style={style}>
            {BAR_HEIGHTS.map((h, i) => (
                <div key={i} className="w-[2.67px] shrink-0 rounded-[2px] bg-[#d6d6d6]" style={{ height: h }} />
            ))}
        </div>
    );
}

/** The "My Contract" resume-paper mockup + photo + caption (front program card visual). */
/** The card's visual: the area's photograph, plus the count of projects under it. */
function AreaVisual({ program }: { program: Program }) {
    const count = PROJECTS.filter((p) => p.programs.includes(program.slug)).length;

    return (
        <div className="relative h-[410px] w-[563px]">
            {/* Card peeking behind, left */}
            <div className="absolute left-[39px] top-[55px] h-[249px] w-[189px] rotate-[-6deg] rounded-[10px] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                <img src={program.icon} alt="" className="absolute left-[16px] top-[16px] size-[22px]" />
                <p className="absolute left-[16px] top-[52px] w-[157px] text-[13px] font-bold leading-[18px] text-[#171717]">
                    {program.lead}
                    {program.bold}
                </p>
                <div className="absolute left-[16px] top-[110px] flex w-[157px] flex-col gap-[10px]">
                    <div className="h-[6px] w-[157px] rounded-full bg-[#e7e7e7]" />
                    <div className="h-[6px] w-[157px] rounded-full bg-[#e7e7e7]" />
                    <div className="h-[6px] w-[101px] rounded-full bg-[#e7e7e7]" />
                </div>
                <p className="absolute left-[16px] top-[196px] text-[11px] font-bold text-[#f60]">
                    {count} project{count === 1 ? '' : 's'}
                </p>
            </div>

            {/* Photograph, foreground */}
            <div className="absolute left-[18px] top-[18px] h-[283px] w-[455px] overflow-hidden rounded-[12px] bg-white shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
                <img src={program.photo} alt="" className="size-full object-cover" />
            </div>

            {/* Caption */}
            <p className="absolute left-[32px] top-[321px] w-[300px] text-[16px] font-bold tracking-[-0.32px] text-[#171717]">
                {program.lead}
                {program.bold}
            </p>
            <p className="absolute left-[32px] top-[355px] w-[465px] text-[12px] tracking-[-0.12px] text-[#6e6e6e]">
                {program.description}
            </p>
        </div>
    );
}

/** The copy + mockup that sits ON one tinted program-card surface, revealed with a stagger. */
function ProgramCardContent({ program, shown }: { program: Program; shown: boolean }) {
    const rv = shown ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0';
    return (
        <>
            {/* Icon */}
            <img
                src={program.icon}
                alt=""
                className={`absolute left-[60px] top-[108px] size-[74px] object-contain ${RV_BASE} ${rv}`}
                style={at(0)}
            />

            {/* Title */}
            <p
                className={`absolute left-[146px] top-[100px] flex h-[89px] items-center whitespace-nowrap text-[48px] font-semibold tracking-[-1px] text-[#151515] ${RV_BASE} ${rv}`}
                style={at(70)}
            >
                <span className="text-[#8d8d8d]">{program.lead}</span>
                {program.bold}
            </p>

            {/* Paragraph */}
            <p
                className={`absolute left-[67px] top-[219px] w-[556px] text-[18px] leading-[24px] text-[#444] ${RV_BASE} ${rv}`}
                style={at(150)}
            >
                {program.description}
            </p>

            {/* Explore button */}
            <Link
                to={`/focus-areas/${program.slug}`}
                className={`absolute left-[67px] top-[340px] flex h-[44px] w-[216px] items-center justify-center rounded-[10px] bg-[#1b1b1b] text-[16px] font-bold leading-[24px] text-white ${RV_BASE} ${rv}`}
                style={at(220)}
            >
                Explore
            </Link>

            {/* Decorative bars */}
            <EqualizerBars
                className={`absolute left-[610px] top-[180px] ${RV_BASE} ${shown ? 'opacity-100' : 'opacity-0'}`}
                style={at(260)}
            />

            {/* Contract mockup */}
            <div className={`absolute left-[986px] top-[62px] ${RV_BASE} ${rv}`} style={at(300)}>
                <AreaVisual program={program} />
            </div>
        </>
    );
}

/** One sticky card in the stack; its content stagger-reveals when it scrolls in. */
function StackedCard({ program, index, last }: { program: Program; index: number; last: boolean }) {
    const ref = useRef<HTMLDivElement>(null);
    const shown = useInView(ref, { threshold: 0.35, once: true });
    return (
        <div
            ref={ref}
            className="sticky"
            style={{
                top: STACK_TOP,
                zIndex: index + 1,
                marginBottom: last ? 0 : '6vh',
            }}
        >
            <FigmaFrame designWidth={1920} designHeight={CARD_H} className="w-full">
                <div className="relative w-[1920px]" style={{ height: CARD_H }}>
                    <div
                        className="absolute left-[157px] top-0 h-[534px] w-[1609px] overflow-hidden rounded-[40px]"
                        style={{ backgroundColor: program.bg }}
                    >
                        <ProgramCardContent program={program} shown={shown} />
                    </div>
                </div>
            </FigmaFrame>
        </div>
    );
}

function ProgramsDesktop({ list }: { list: Program[] }) {
    return (
        <section className="bg-[#ececec]">
            {/* Heading band — pinned to the top so the title and subtitle stay
                visible while the cards stack in the space beneath it. */}
            <div className="sticky top-0 z-20 bg-[#ececec]">
                <FigmaFrame designWidth={1920} designHeight={HEAD_H} className="w-full">
                    <div className="relative h-[296px] w-[1920px] bg-[#ececec]">
                        <Reveal
                            as="p"
                            once
                            className="absolute left-[154px] top-[150px] text-[96px] font-extrabold leading-[103px] tracking-[-1px]"
                        >
                            <span className="font-normal text-[#6c6c6c]">Our </span>
                            <span className="text-[#1b1b1b]">Programs</span>
                        </Reveal>
                        <Reveal
                            as="p"
                            once
                            delay={120}
                            className="absolute left-[863px] top-[148px] w-[621px] text-[18px] font-bold leading-[25px] text-[#333]"
                        >
                            {SUBHEADING}
                        </Reveal>
                    </div>
                </FigmaFrame>
            </div>

            {/* Stacking cards: each pins just below the heading and the next slides
                up and stacks over it — all inside the space under the title. */}
            <div className="relative">
                {list.map((program, i) => (
                    <StackedCard key={program.slug} program={program} index={i} last={i === list.length - 1} />
                ))}
            </div>

            {/* Trailing runway so the completed stack settles before the next section */}
            <div className="h-[16vh]" />
        </section>
    );
}

/** One sticky mobile card; its content stagger-reveals as the card scrolls in. */
function StackedCardMobile({ program, index, last }: { program: Program; index: number; last: boolean }) {
    const ref = useRef<HTMLDivElement>(null);
    const shown = useInView(ref, { threshold: 0.35, once: true });
    const rv = shown ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0';

    return (
        <div
            ref={ref}
            className="sticky"
            style={{
                top: 'calc(var(--nav-height) + 12px)',
                zIndex: index + 1,
                marginBottom: last ? 0 : '4vh',
            }}
        >
            {/* Nearly a full screen tall, so the next card stays hidden until it
                scrolls up and stacks over this one. */}
            <div
                className="flex flex-col rounded-[21px] p-5"
                style={{
                    backgroundColor: program.bg,
                    minHeight: 'calc(100dvh - var(--nav-height) - 34px)',
                }}
            >
                <div className={`flex items-center gap-[6px] ${RV_BASE} ${rv}`} style={at(0)}>
                    <img src={program.icon} alt="" className="size-[35px] object-contain" />
                    <h3 className="text-[24px] font-semibold leading-[32px] tracking-[-1px] text-[#151515]">
                        <span className="text-[#8d8d8d]">{program.lead}</span>
                        {program.bold}
                    </h3>
                </div>

                {/* The <img> is the flex item itself (as in Innovations) so it grows
                    to fill the card — a wrapper div leaves the image short. */}
                <img
                    src={program.photo}
                    alt=""
                    className={`mt-4 min-h-[131px] w-full flex-1 rounded-[6px] object-cover ${RV_BASE} ${rv}`}
                    style={at(120)}
                />

                <p
                    className={`mt-4 text-[14px] font-normal leading-[24px] text-[#151515] ${RV_BASE} ${rv}`}
                    style={at(200)}
                >
                    {program.description}
                </p>

                <Link
                    to={`/focus-areas/${program.slug}`}
                    className={`mt-5 flex w-full shrink-0 items-center justify-center rounded-[10px] bg-[#1b1b1b] px-5 py-[10px] text-[16px] font-bold leading-[24px] text-white ${RV_BASE} ${rv}`}
                    style={at(280)}
                >
                    Explore
                </Link>
            </div>
        </div>
    );
}

function ProgramsMobile({ list }: { list: Program[] }) {
    return (
        <section className="bg-[#e5e5e5] px-5 pb-[10vh] pt-12">
            <h2 className="font-extrabold leading-[50px] tracking-[-1px]">
                <span className="text-[40px] font-normal text-[#6c6c6c]">Our </span>
                <span className="text-[40px] text-[#1b1b1b]">Programs</span>
            </h2>
            <p className="mt-4 text-[14px] font-normal leading-[24px] text-[#333]">{SUBHEADING}</p>

            {/* Stacking cards: each pins below the navbar and the next covers it */}
            <div className="relative mt-9">
                {list.map((program, i) => (
                    <StackedCardMobile key={program.slug} program={program} index={i} last={i === list.length - 1} />
                ))}
            </div>
        </section>
    );
}

export default function Programs() {
    const list = programs();
    if (!list.length) return null;
    return (
        <>
            <div className="hidden lg:block">
                <ProgramsDesktop list={list} />
            </div>
            <div className="lg:hidden">
                <ProgramsMobile list={list} />
            </div>
        </>
    );
}
